// Compose a book cover: draw the image (cover-fit) and overlay title + author.
// Fully client-side using canvas. Returns a JPEG data URL.

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = (text || "").trim().split(/\s+/);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

export async function composeCover(imageSrc, { title = "", author = "", position = "bottom", overlay = true }) {
  const W = 800;
  const H = 1200;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  const img = await loadImage(imageSrc);
  const scale = Math.max(W / img.width, H / img.height);
  const dw = img.width * scale;
  const dh = img.height * scale;
  ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);

  if (!overlay || (!title && !author)) {
    return canvas.toDataURL("image/jpeg", 0.92);
  }

  try {
    await document.fonts.load('700 76px "Cormorant Garamond"');
    await document.fonts.load('500 30px "Manrope"');
    await document.fonts.ready;
  } catch (_) { /* fonts best-effort */ }

  const margin = 76;
  const cx = W / 2;
  const lineH = 84;

  ctx.font = '700 76px "Cormorant Garamond", Georgia, serif';
  const lines = wrapText(ctx, title, W - margin * 2);

  let blockH = lines.length * lineH;
  if (author) blockH += 34 + 46; // divider gap + author line

  let topY;
  if (position === "top") topY = 70;
  else if (position === "center") topY = (H - blockH) / 2;
  else topY = H - 90 - blockH; // bottom

  // Scrim for readability
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  if (position === "top") {
    grad.addColorStop(0, "rgba(8,13,26,0.92)");
    grad.addColorStop(0.42, "rgba(8,13,26,0)");
  } else if (position === "center") {
    ctx.fillStyle = "rgba(8,13,26,0.45)";
    ctx.fillRect(0, 0, W, H);
    grad.addColorStop(0, "rgba(8,13,26,0)");
    grad.addColorStop(0.5, "rgba(8,13,26,0.35)");
    grad.addColorStop(1, "rgba(8,13,26,0)");
  } else {
    grad.addColorStop(0.5, "rgba(8,13,26,0)");
    grad.addColorStop(1, "rgba(8,13,26,0.94)");
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Draw text block downward from topY
  ctx.textAlign = "center";
  ctx.shadowColor = "rgba(0,0,0,0.55)";
  ctx.shadowBlur = 12;
  let y = topY;

  ctx.font = '700 76px "Cormorant Garamond", Georgia, serif';
  ctx.fillStyle = "#f8fafc";
  for (const line of lines) {
    y += 76;
    ctx.fillText(line, cx, y);
    y += 8;
  }

  if (author) {
    y += 34;
    ctx.shadowBlur = 0;
    ctx.strokeStyle = "rgba(216,179,106,0.85)";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx - 44, y);
    ctx.lineTo(cx + 44, y);
    ctx.stroke();
    y += 46;
    ctx.shadowColor = "rgba(0,0,0,0.55)";
    ctx.shadowBlur = 8;
    ctx.font = '500 30px "Manrope", sans-serif';
    if ("letterSpacing" in ctx) ctx.letterSpacing = "6px";
    ctx.fillStyle = "#e0c078";
    ctx.fillText(author.toUpperCase(), cx, y);
    if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
  }

  ctx.shadowBlur = 0;
  return canvas.toDataURL("image/jpeg", 0.92);
}

function wrapPlain(ctx, text, maxWidth) {
  // wrap preserving explicit line breaks
  const paragraphs = (text || "").split(/\n+/);
  const out = [];
  for (const para of paragraphs) {
    const words = para.trim().split(/\s+/).filter(Boolean);
    if (!words.length) { out.push(""); continue; }
    let cur = "";
    for (const w of words) {
      const test = cur ? `${cur} ${w}` : w;
      if (ctx.measureText(test).width > maxWidth && cur) { out.push(cur); cur = w; }
      else cur = test;
    }
    if (cur) out.push(cur);
  }
  return out;
}

export async function composeBackCover(baseImage, { title = "", synopsis = "", author = "", imprint = "" }) {
  const W = 800;
  const H = 1200;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background: darkened front cover if available, else editorial gradient
  if (baseImage) {
    try {
      const img = await loadImage(baseImage);
      const scale = Math.max(W / img.width, H / img.height);
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.filter = "blur(6px)";
      ctx.drawImage(img, (W - dw) / 2, (H - dh) / 2, dw, dh);
      ctx.filter = "none";
      ctx.fillStyle = "rgba(8,13,26,0.88)";
      ctx.fillRect(0, 0, W, H);
    } catch (_) {
      baseImage = null;
    }
  }
  if (!baseImage) {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, "#101b33");
    g.addColorStop(1, "#080d1a");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  try {
    await document.fonts.load('700 46px "Cormorant Garamond"');
    await document.fonts.load('400 24px "Manrope"');
    await document.fonts.load('500 26px "Manrope"');
    await document.fonts.ready;
  } catch (_) { /* best effort */ }

  const margin = 90;
  const cx = W / 2;

  // Title (top)
  ctx.textAlign = "center";
  ctx.fillStyle = "#e0c078";
  ctx.font = '700 46px "Cormorant Garamond", serif';
  const titleLines = wrapText(ctx, title, W - margin * 2);
  let y = 150;
  for (const line of titleLines.slice(0, 2)) {
    ctx.fillText(line, cx, y);
    y += 54;
  }

  // Divider
  y += 8;
  ctx.strokeStyle = "rgba(216,179,106,0.6)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(cx - 60, y);
  ctx.lineTo(cx + 60, y);
  ctx.stroke();
  y += 56;

  // Synopsis (left aligned, justified feel)
  ctx.textAlign = "left";
  ctx.fillStyle = "#cbd5e1";
  ctx.font = '400 24px "Manrope", sans-serif';
  const lineH = 38;
  const maxSynY = H - 220;
  const lines = wrapPlain(ctx, synopsis, W - margin * 2);
  for (let i = 0; i < lines.length; i++) {
    if (y > maxSynY) {
      ctx.fillText("…", margin, y);
      break;
    }
    ctx.fillText(lines[i], margin, y);
    y += lines[i] === "" ? lineH * 0.6 : lineH;
  }

  // Author + imprint (bottom)
  ctx.textAlign = "center";
  if (author) {
    ctx.fillStyle = "#f8fafc";
    ctx.font = '500 28px "Manrope", sans-serif';
    if ("letterSpacing" in ctx) ctx.letterSpacing = "4px";
    ctx.fillText(author.toUpperCase(), cx, H - 130);
    if ("letterSpacing" in ctx) ctx.letterSpacing = "0px";
  }
  // faux barcode block
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  const bw = 150, bh = 60, bx = cx - bw / 2, by = H - 100;
  ctx.fillRect(bx, by, bw, bh);
  ctx.fillStyle = "#0a0f1c";
  for (let i = 0; i < 28; i++) {
    const w = (i % 4 === 0) ? 4 : 2;
    ctx.fillRect(bx + 10 + i * 4.7, by + 8, w, bh - 26);
  }
  ctx.fillStyle = "#0a0f1c";
  ctx.font = '400 10px "Manrope", monospace';
  ctx.fillText("9  780000  000000", cx, by + bh - 5);

  if (imprint) {
    ctx.fillStyle = "rgba(216,179,106,0.7)";
    ctx.font = '400 15px "Manrope", sans-serif';
    ctx.fillText(imprint, cx, H - 26);
  }

  return canvas.toDataURL("image/jpeg", 0.92);
}
